import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import { SchemaClientKinds, Source } from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";
import { uniqueSortedLabels } from "./filterValues";

/** One row of the filter-lookup table under the `maintenancePerformedBy` partition. */
export type Maintainer = {
  filterValue: string;
};

/**
 * Maintainers, read from the same filter-lookup table as `Tags.ts` but from the
 * `maintenancePerformedBy` partition rather than `tags`.
 *
 * GraphQL only; see {@link getMaintainers}.
 */
const listMaintainers: Source<Maintainer[]> = {
  graphql: parse(`
    query listIRFilterLookups($nextToken: String) {
      listIRFilterLookups(
        limit: 1000,
        nextToken: $nextToken,
        filterAttribute: "maintenancePerformedBy",
        sortDirection: ASC
      ) {
        nextToken
        items {
          filterValue
        }
      }
    }
  `),
};

/**
 * Maintainer filter options.
 *
 * Answers `[]` for any client that is not GraphQL. TODO: no DynamoDB request is declared yet.
 * The physical table is the one `DynamoDbTables.TAGS` already names — the lookup table
 * partitions by `filterAttribute` — so this is the cheapest of the four to complete later.
 * Safe as a stub because these values only populate a grid filter dropdown and never reach
 * `DataManager`.
 *
 * @param client - Read client for the master data.
 * @returns Maintainer names, deduplicated and sorted.
 */
export const getMaintainers = async (client: SchemaClient): Promise<string[]> => {
  if (client.kind !== SchemaClientKinds.GRAPHQL) {
    return [];
  }
  const maintainers = await client.queryList(listMaintainers);
  const items = maintainers === EMPTY_VALUE ? [] : maintainers ?? [];
  return uniqueSortedLabels(items.map((maintainers) => maintainers.filterValue));
};
